import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { notificationService } from '../../services/notificationService';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '../../utils/formatters';
import { LynkSeal, LynkWordmark } from './LynkLogo';
import ConferenceBar from './ConferenceBar';

const UniversalNavbar = () => {
  const { t } = useTranslation('common');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, userProfile, signOut } = useAuth();
  const { theme, setTheme, isDark } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);
  const mobileNotifRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const isActive = (path) => location?.pathname === path;

  // The landing hero is ink in both themes, so the navbar sits transparently
  // over it at the top of the page and solidifies once you scroll away.
  const isLandingPage = ['/', '/modern-landing-page']?.includes(location?.pathname);
  /* Was `isLandingPage && !isScrolled && …` — the navbar went dark-on-dark
     while it sat over the old near-black hero. The hero is parchment now, so
     that would float a Night bar over a light page. The navbar follows the
     theme everywhere, and the landing page gets a masthead hairline instead
     of a colour change. `isLandingPage` is still used for the scroll state. */
  const isOverHero = false;

  useEffect(() => {
    if (!isLandingPage) { setIsScrolled(false); return; }
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isLandingPage]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const loadNotifications = async () => {
      const [notifs, count] = await Promise.all([
        notificationService?.getUserNotifications(10),
        notificationService?.getUnreadCount()
      ]);
      setNotifications(notifs);
      setUnreadCount(count);
    };
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedDesktopNotifications = notifRef?.current?.contains(e?.target);
      const clickedMobileNotifications = mobileNotifRef?.current?.contains(e?.target);

      if (!clickedDesktopNotifications && !clickedMobileNotifications) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAllRead = async () => {
    await notificationService?.markAllAsRead();
    setNotifications(prev => prev?.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  const handleNotificationClick = async (notif) => {
    if (!notif?.isRead) {
      await notificationService?.markAsRead(notif?.id);
      setNotifications(prev => prev?.map(n => n?.id === notif?.id ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setShowNotifications(false);
    // Open the notification in the inbox and focus it there.
    navigate(`/notifications?focus=${notif?.id}`);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setActiveDropdown(null);
    setShowNotifications(false);
  };

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const handleLogout = async () => {
    const { error } = await signOut();
    if (!error) navigate('/modern-landing-page');
  };

  const publicNavItems = [
    { path: '/modern-landing-page', label: t('nav.home'), icon: 'Home' },
    { path: '/public-conference-board', label: t('nav.conferences'), icon: 'Search' },
  ];

  const isAdmin = userProfile?.isPlatformAdmin ?? userProfile?.roles?.includes('admin');

  // Admin gets direct nav links instead of dropdowns
  const adminNavLinks = isAdmin ? [
    { path: '/master-admin-dashboard', label: t('nav.platformDashboard'), icon: 'LayoutDashboard' },
    { path: '/admin-user-management-system', label: t('nav.userManagement'), icon: 'Users' },
    { path: '/admin-conference-management-interface', label: t('nav.manageConferences'), icon: 'FolderOpen' },
  ] : [];

  /**
   * Global navigation carries only what crosses conferences.
   *
   * It used to hide four links behind two dropdowns that each held exactly two
   * items — two clicks to reach anything, and a menu whose only job was to
   * conceal a pair of links. Per-conference work now lives in the conference
   * bar below, where it belongs, so what's left here is genuinely global: the
   * cross-conference views. Flat, and short enough to stay flat.
   */
  const memberNavLinks = [
    { path: '/user-dashboard', label: t('nav.dashboard'), icon: 'LayoutDashboard' },
    { path: '/public-conference-board', label: t('nav.conferences'), icon: 'Search' },
    { path: '/my-submissions', label: t('nav.mySubmissions'), icon: 'FileText' },
    { path: '/reviewer-dashboard', label: t('nav.reviews'), icon: 'ClipboardList' },
  ];

  // The navbar is chrome (dark) in both themes, so link colours come from the
  // chrome token set rather than the work-surface tokens.
  /* Active state is a 2px underline, not a tinted pill — and the ink has to
     track the surface. On dark chrome the underline is gilt, per the reference
     sheet (`inset 0 -2px 0 #C9A227`); grenat there is 1.2:1 and simply does not
     appear. On a light navbar it is primary. */
  const navLinkClass = (path) =>
    `inline-flex items-center h-14 px-3 -mb-px border-b-2 text-sm font-medium transition-colors focus-ring ${
      isActive(path)
        ? isOverHero || isDark
          ? 'border-chrome-accent text-chrome-foreground'
          : 'border-primary text-primary'
        : isOverHero
          ? 'border-transparent text-chrome-muted hover:text-chrome-foreground hover:bg-chrome-hover'
          : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
    }`;

  const renderNotificationsPanel = (panelClassName) => (
    <div className={panelClassName}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-xs font-semibold text-foreground">{t('notifications.title')}</span>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllRead} className="text-xs text-primary hover:underline">
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>
      <div className="max-h-72 overflow-y-auto">
        {notifications?.length === 0 ? (
          <div className="py-6 text-center">
            <Icon name="BellOff" size={18} className="mx-auto text-muted-foreground mb-1" />
            <p className="text-xs text-muted-foreground">{t('notifications.empty')}</p>
          </div>
        ) : (
          notifications?.map((notif) => (
            <button
              key={notif?.id}
              onClick={() => handleNotificationClick(notif)}
              className={`w-full text-start px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/50 transition-colors ${
                !notif?.isRead ? 'bg-primary/3' : ''
              }`}
            >
              <div className="flex items-start gap-2">
                <div className={`mt-1.5 w-1.5 h-1.5 flex-shrink-0 ${!notif?.isRead ? 'bg-primary' : 'bg-transparent'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{notif?.subject}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{notif?.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{formatRelativeTime(notif?.createdAt)}</p>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
      <button
        onClick={() => { setShowNotifications(false); navigate('/notifications'); }}
        className="w-full text-center px-3 py-2 border-t border-border text-xs font-medium text-primary hover:bg-muted/50 transition-colors"
      >
        {t('notifications.seeAll', 'See all notifications')}
      </button>
    </div>
  );

  return (
    <>
      {/* App chrome follows the theme. Over the landing hero it goes dark to
          merge with it; elsewhere it matches the surface it sits on, so the
          logo uses the correct tone for the current theme. */}
      <nav
        className={`sticky top-0 z-header transition-colors duration-300 ${
          isOverHero
            ? 'bg-chrome border-b border-chrome-border'
            : 'bg-card border-b border-border'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            {/* Home means "my home". Signed in, that's the dashboard; signed
                out, the landing page. It previously always went to the public
                board, which is neither. */}
            <Link
              to={isAuthenticated ? (isAdmin ? '/master-admin-dashboard' : '/user-dashboard') : '/modern-landing-page'}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              {/* Banded wordmark on desktop; the split icon takes over on
                  mobile, where the wordmark would fall below its 88px minimum
                  and the band's cut stops reading.
                  `tone` must track the surface the navbar is actually painted
                  with — the band inverts to --lynk-mark-paper, so a mismatch
                  makes the cut look wrong. */}
              {/* Wrapped in plain spans on purpose: lynk-logo.css is unlayered,
                  so its `display` beats Tailwind's layered `hidden`/`sm:*`
                  utilities. Toggling a wrapper sidesteps the cascade fight. */}
              {/* Under sm the slot is icon-sized, and the seal is the only mark
                  that survives there. Gilt is dark-ground only — on a light
                  navbar it is 2.1:1 — so the variant tracks the surface. */}
              <span className="inline-flex sm:hidden">
                <LynkSeal size={28} variant={isOverHero || isDark ? 'gilt' : 'grenat'} />
              </span>
              <span className="hidden sm:inline-flex">
                <LynkWordmark size={20} ink="var(--color-mark)" />
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {!isAuthenticated ? (
                <>
                  {publicNavItems?.map((item) => (
                    <Link key={item?.path} to={item?.path} className={navLinkClass(item?.path)}>
                      {item?.label}
                    </Link>
                  ))}
                  <div className="ltr:ml-4 rtl:mr-4 flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/user-sign-in')}>
                      {t('nav.signIn')}
                    </Button>
                    <Button size="sm" onClick={() => navigate('/user-registration')}>
                      {t('nav.register')}
                    </Button>
                    <LanguageSwitcher />
                    <button
                      onClick={() => setTheme(isDark ? 'light' : 'dark')}
                      className={`p-1.5 rounded-md transition-colors ${isOverHero ? "text-chrome-muted hover:text-chrome-foreground hover:bg-chrome-hover" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                      title={isDark ? t('theme.switchToLight') : t('theme.switchToDark')}
                    >
                      <Icon name={isDark ? 'Sun' : 'Moon'} size={16} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {isAdmin ? (
                    <>
                      {/* Admin mode badge */}
                      <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-primary/10 text-primary border border-primary/20 select-none">
                        <Icon name="Shield" size={12} className="inline ltr:mr-1 rtl:ml-1 -mt-0.5" />
                        {t('nav.admin')}
                      </span>
                      {/* Admin direct nav links */}
                      {adminNavLinks?.map((item) => (
                        <Link key={item?.path} to={item?.path} className={navLinkClass(item?.path)}>
                          {item?.label}
                        </Link>
                      ))}
                    </>
                  ) : (
                    memberNavLinks?.map((item) => (
                      <Link key={item?.path} to={item?.path} className={navLinkClass(item?.path)}>
                        {item?.label}
                      </Link>
                    ))
                  )}

                  {/* Creating a conference is an action, not a place. It reads
                      as a button so it stops competing with navigation for the
                      same visual weight. */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="ltr:ml-2 rtl:mr-2"
                    iconName="Plus"
                    iconPosition="left"
                    iconSize={14}
                    onClick={() => navigate('/conference-setup-wizard')}
                  >
                    {t('nav.createConference')}
                  </Button>

                  {/* Notifications */}
                  <div className="relative ltr:ml-2 rtl:mr-2" ref={notifRef}>
                    <button
                      onClick={() => { setShowNotifications(!showNotifications); setActiveDropdown(null); }}
                      className={`relative p-1.5 rounded-md transition-colors ${isOverHero ? "text-chrome-muted hover:text-chrome-foreground hover:bg-chrome-hover" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                    >
                      <Icon name="Bell" size={16} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 ltr:-right-0.5 rtl:-left-0.5 w-4 h-4 bg-error text-white text-[10px] font-bold flex items-center justify-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>

                    {showNotifications && renderNotificationsPanel('absolute top-full ltr:right-0 rtl:left-0 mt-1 w-72 bg-card border border-border rounded-md shadow-elevation-3 z-dropdown overflow-hidden motion-rise')}
                  </div>

                  {/* User Menu */}
                  <div className="relative ltr:ml-1 rtl:mr-1">
                    <button
                      onClick={() => toggleDropdown('user')}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-muted transition-colors"
                    >
                      <div className="w-6 h-6 bg-primary/10 text-primary flex items-center justify-center">
                        <Icon name="User" size={12} />
                      </div>
                      <span className={`text-sm font-medium max-w-[100px] truncate hidden xl:inline ${isOverHero ? "text-chrome-foreground" : "text-foreground"}`}>
                        {userProfile?.firstName || 'User'}
                      </span>
                      <Icon name="ChevronDown" size={12} className={isOverHero ? "text-chrome-muted" : "text-muted-foreground"} />
                    </button>

                    {activeDropdown === 'user' && (
                      <div className="absolute top-full ltr:right-0 rtl:left-0 mt-1 w-44 bg-card border border-border rounded-md shadow-elevation-3 py-1 z-dropdown motion-rise">
                        <div className="px-3 py-2 border-b border-border">
                          <p className="text-xs font-medium text-foreground truncate">
                            {userProfile?.firstName && userProfile?.lastName
                              ? `${userProfile?.firstName} ${userProfile?.lastName}`
                              : userProfile?.email || 'User'}
                          </p>
                          <p className="text-[10px] text-muted-foreground truncate">{userProfile?.email}</p>
                        </div>
                        <Link
                          to="/user-profile-management"
                          onClick={() => setActiveDropdown(null)}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          <Icon name="User" size={14} />
                          {t('nav.profile')}
                        </Link>
                        <Link
                          to={isAdmin ? '/master-admin-dashboard' : '/user-dashboard'}
                          onClick={() => setActiveDropdown(null)}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                        >
                          <Icon name={isAdmin ? 'Shield' : 'LayoutDashboard'} size={14} />
                          {t('nav.dashboard')}
                        </Link>
                        <div className="border-t border-border my-0.5"></div>
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5 transition-colors w-full text-start"
                        >
                          <Icon name="LogOut" size={14} />
                          {t('nav.signOut')}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Language Switcher */}
                  <LanguageSwitcher />

                  {/* Theme Toggle */}
                  <button
                    onClick={() => setTheme(isDark ? 'light' : 'dark')}
                    className="ltr:ml-1 rtl:mr-1 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title={isDark ? t('theme.switchToLight') : t('theme.switchToDark')}
                  >
                    <Icon name={isDark ? 'Sun' : 'Moon'} size={16} />
                  </button>
                </>
              )}
            </div>

            {/* Mobile Actions */}
            <div className="lg:hidden flex items-center gap-1">
              {isAuthenticated && (
                <div className="relative" ref={mobileNotifRef}>
                  <button
                    onClick={() => { setShowNotifications(!showNotifications); setActiveDropdown(null); setIsMobileMenuOpen(false); }}
                    className={`relative p-1.5 rounded-md transition-colors ${isOverHero ? "text-chrome-muted hover:text-chrome-foreground hover:bg-chrome-hover" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                    aria-label={t('notifications.title')}
                  >
                    <Icon name="Bell" size={18} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 ltr:-right-0.5 rtl:-left-0.5 w-4 h-4 bg-error text-white text-[10px] font-bold flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {showNotifications && renderNotificationsPanel('absolute top-full right-0 mt-1 w-[min(20rem,calc(100vw-2rem))] bg-card border border-border rounded-md shadow-elevation-3 z-dropdown overflow-hidden motion-rise')}
                </div>
              )}

              <button
                onClick={toggleMobileMenu}
                className={`p-1.5 rounded-md transition-colors ${isOverHero ? "text-chrome-muted hover:text-chrome-foreground hover:bg-chrome-hover" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-card motion-rise">
            <div className="px-4 py-2 space-y-0.5">
              {!isAuthenticated ? (
                <>
                  {publicNavItems?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      onClick={toggleMobileMenu}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive(item?.path) ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={item?.icon} size={16} />
                      {item?.label}
                    </Link>
                  ))}
                  <div className="pt-2 flex gap-2">
                    <LanguageSwitcher />
                    <button
                      onClick={() => setTheme(isDark ? 'light' : 'dark')}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground hover:bg-muted transition-colors"
                      title={isDark ? t('theme.switchToLight') : t('theme.switchToDark')}
                    >
                      <Icon name={isDark ? 'Sun' : 'Moon'} size={16} />
                    </button>
                    <Button variant="outline" size="sm" fullWidth onClick={() => { toggleMobileMenu(); navigate('/user-sign-in'); }}>
                      {t('nav.signIn')}
                    </Button>
                    <Button size="sm" fullWidth onClick={() => { toggleMobileMenu(); navigate('/user-registration'); }}>
                      {t('nav.register')}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {isAdmin && (
                    <div className="px-3 py-1.5 mb-1">
                      <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-primary/10 text-primary border border-primary/20 select-none">
                        <Icon name="Shield" size={12} className="inline ltr:mr-1 rtl:ml-1 -mt-0.5" />
                        {t('nav.admin')}
                      </span>
                    </div>
                  )}

                  {isAdmin ? (
                    <>
                      {adminNavLinks?.map((item) => (
                        <Link key={item?.path} to={item?.path} onClick={toggleMobileMenu}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive(item?.path) ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                          }`}
                        >
                          <Icon name={item?.icon} size={16} />
                          {item?.label}
                        </Link>
                      ))}
                    </>
                  ) : (
                    memberNavLinks?.map((item) => (
                      <Link key={item?.path} to={item?.path} onClick={toggleMobileMenu}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive(item?.path) ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={16} />
                        {item?.label}
                      </Link>
                    ))
                  )}

                  <Link to="/conference-setup-wizard" onClick={toggleMobileMenu}
                    className="flex items-center gap-2 px-3 py-2 mt-1 rounded-md text-sm font-medium text-primary hover:bg-primary/5 transition-colors"
                  >
                    <Icon name="Plus" size={16} />
                    {t('nav.createConference')}
                  </Link>

                  <div className="border-t border-border pt-2 mt-2">
                    <div className="px-3 py-1.5 flex items-center justify-between">
                      <p className="text-xs font-medium text-foreground truncate">
                        {userProfile?.firstName && userProfile?.lastName
                          ? `${userProfile?.firstName} ${userProfile?.lastName}`
                          : userProfile?.email || 'User'}
                      </p>
                      <div className="flex items-center gap-1">
                        <LanguageSwitcher />
                        <button
                          onClick={() => setTheme(isDark ? 'light' : 'dark')}
                          className={`p-1.5 rounded-md transition-colors ${isOverHero ? "text-chrome-muted hover:text-chrome-foreground hover:bg-chrome-hover" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                          title={isDark ? t('theme.switchToLight') : t('theme.switchToDark')}
                        >
                          <Icon name={isDark ? 'Sun' : 'Moon'} size={14} />
                        </button>
                      </div>
                    </div>
                    <Link to="/user-profile-management" onClick={toggleMobileMenu}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground hover:bg-muted"
                    >
                      <Icon name="User" size={14} />
                      {t('nav.profile')}
                    </Link>
                    <button
                      onClick={() => { toggleMobileMenu(); handleLogout(); }}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-error hover:bg-error/5 w-full text-start"
                    >
                      <Icon name="LogOut" size={14} />
                      {t('nav.signOut')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Conference context. Renders nothing unless a conference is actually in
          scope, so mounting it here gives all 31 screens the bar without each
          page having to opt in. */}
      <ConferenceBar />

      {activeDropdown && (
        <div className="fixed inset-0 z-dropdown-overlay" onClick={() => setActiveDropdown(null)} />
      )}
    </>
  );
};

export default UniversalNavbar;
